// Pure helpers for the Releases pixelation/mosaic effect. Kept React-free so
// the draw logic and palette extraction can be unit-tested directly and reused
// by both the on-screen canvas layer and (potentially) export tooling.

export type Rgb = {r: number; g: number; b: number};

/** Deterministic PRNG (mulberry32). Seeded so a given (seed, params) tuple
 * always paints the same mosaic — the preview stays stable across React
 * re-renders, and the exported PNG matches what's on screen. Regenerate just
 * bumps the seed. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Deterministic 32-bit hash of an integer triple. Used to seed a per-cell RNG
 * keyed on the cell's (fadeIndex, crossIndex) (see computeCell). Since every
 * template shares the fade-cell count, a given index pair lands at the same
 * point in the dissolve everywhere, so the dither lines up across templates and
 * is identical for the base/@2x splash variants. Stable across React re-renders
 * and matches the exported PNG; Regenerate bumps the seed. */
export function hashInts(a: number, b: number, c: number): number {
  let h = a >>> 0;
  h = Math.imul(h ^ (b + 0x9e3779b9), 0x85ebca77) >>> 0;
  h = Math.imul(h ^ (c + 0x9e3779b9), 0xc2b2ae3d) >>> 0;
  h ^= h >>> 15;
  return h >>> 0;
}

export function hexToRgb(hex: string): Rgb {
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const n = parseInt(h, 16);
  if (h.length !== 6 || Number.isNaN(n)) return {r: 0, g: 0, b: 0};
  return {r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255};
}

/**
 * Extract a small dominant-color palette from an image URL by quantizing into
 * 4-bit-per-channel buckets and returning the most frequent ones (bucket
 * averages). Used for the "background image" palette source.
 *
 * Returns [] on any failure — a tainted canvas (CORS), a missing image, or an
 * empty result. Callers fall back to the product palette so the effect never
 * silently disappears. The bg <img> is loaded with crossOrigin="anonymous"
 * (BgTransformer sets it, same as the export path), so CDN images sample fine.
 */
export async function extractPaletteFromImageUrl(
  url: string,
  maxColors = 12
): Promise<string[]> {
  if (!url) return [];
  const img = await loadImage(url).catch(() => null);
  if (!img) return [];

  const SAMPLE = 80; // small enough to be cheap, large enough to be representative
  const w = SAMPLE;
  const h = Math.max(1, Math.round((img.naturalHeight / img.naturalWidth) * SAMPLE)) || SAMPLE;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", {willReadFrequently: true});
  if (!ctx) return [];
  ctx.drawImage(img, 0, 0, w, h);

  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, w, h).data;
  } catch {
    // Tainted canvas — cross-origin image without usable CORS headers.
    return [];
  }

  // Bucket by the top 4 bits of each channel (4096 buckets max), accumulating
  // summed channel values + counts so we can emit each bucket's average color.
  const buckets = new Map<number, {r: number; g: number; b: number; n: number}>();
  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3];
    if (alpha < 128) continue; // skip transparent pixels
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.r += r;
      bucket.g += g;
      bucket.b += b;
      bucket.n += 1;
    } else {
      buckets.set(key, {r, g, b, n: 1});
    }
  }

  return Array.from(buckets.values())
    .sort((a, b) => b.n - a.n)
    .slice(0, maxColors)
    .map(({r, g, b, n}) => rgbToHex(Math.round(r / n), Math.round(g / n), Math.round(b / n)));
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    "#" +
    [r, g, b]
      .map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0"))
      .join("")
  );
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** The four fade directions, in clockwise 90° rotation order: left→right,
 * top→bottom, right→left, bottom→top. Rotating past the last wraps back to the
 * first. "ltr" is the default every template starts at; the per-template rotate
 * control steps through this order one quarter-turn at a time. */
export const PIXELATION_DIRECTIONS = ["ltr", "ttb", "rtl", "btt"] as const;

export type PixelationDirection = (typeof PIXELATION_DIRECTIONS)[number];

/** The next direction one 90° clockwise step around PIXELATION_DIRECTIONS,
 * wrapping from the last back to the first. */
export function nextPixelationDirection(dir: PixelationDirection): PixelationDirection {
  const i = PIXELATION_DIRECTIONS.indexOf(dir);
  return PIXELATION_DIRECTIONS[(i + 1) % PIXELATION_DIRECTIONS.length];
}

export type PixelationParams = {
  /** Cell edge along the DISSOLVE (fade) axis, in base (unzoomed) pixels. Carries
   * the shared fade-cell count so patterns align across templates. */
  size: number;
  /** Cell edge along the FULL-SPAN (cross) axis, in base pixels. Fit to the
   * cross extent so the last row/column isn't cropped. Defaults to `size`
   * (square cells) when omitted. */
  sizeCross?: number;
  /** Max fill opacity, 0–100. */
  opacity: number;
  /** Fraction of the coverage axis the mosaic spans, 0–100, measured from the
   * solid edge. For "ltr" the axis is width (measured from the left); for "btt"
   * it is height (measured from the bottom); for "rtl" from right; for "ttb"
   * from top. */
  coverage: number;
  /** Direction the mosaic grows and fades along. "ltr" (default): solid at the
   * left edge, dissolving toward the right — used for landscape banners. "btt":
   * solid at the bottom edge, dissolving toward the top — used for square and
   * portrait banners. "rtl": solid at the right edge, dissolving toward the
   * left. "ttb": solid at the top edge, dissolving toward the bottom. */
  direction?: PixelationDirection;
  /** When true, the mosaic scatter-dissolves toward the fade edge of its
   * coverage (squares randomly drop out, denser toward the edge). */
  gradual: boolean;
  /** When true, each row/column toward the fade edge is drawn more transparent
   * than the last — a smooth opacity ramp that fades to nothing at the coverage
   * edge. Independent of `gradual`; the two compose. */
  opacityRamp: boolean;
  /** Palette of hex colors squares are drawn from. */
  palette: string[];
  /** RNG seed (bumped by Regenerate). */
  seed: number;
  /** Per-cell click counts, keyed by `cellKey(row, col)`. Each click steps the
   * square's opacity through a 25% cycle (see `overridePercent`), overriding
   * the generated opacity/fade/ramp. Keyed on grid coordinates (not pixels) so
   * clicks survive zoom and map to the same square in the base↔@2x variants. */
  overrides?: Readonly<Record<string, number>>;
  /** Per-cell color-cycle counts, keyed by `cellKey(row, col)`. Each click
   * advances the square one step through the palette: drawn color index is
   * `(baseIndex + count) % palette.length`. Independent of `overrides`, so a
   * square can carry both an opacity and a color override. */
  colorOverrides?: Readonly<Record<string, number>>;
  /** Brush mask value (0–1) at a canvas point (libs/pixelationMask.ts). A
   * square takes the value at its centre as an alpha factor. Absent = 1. */
  mask?: (x: number, y: number) => number;
  /** The shape of the tiles: "square" (no round corners), "circle" (all four
   * corners round) or "mixed" (each corner round by chance). Absent = square. */
  tileShape?: TileShape;
  /** Mixed tiles: the chance (0–100 %) that one corner is round. */
  tileRoundness?: number;
  /** The radius of a round corner, % of the short side of the tile. 50 with
   * all corners round gives a circle; 100 on one corner gives a quarter disc. */
  tileRadius?: number;
  /** Per-cell corner masks set by clicks, keyed by `cellKey(row, col)`. A
   * value replaces the generated corners of that tile (see CORNER_*). */
  shapeOverrides?: Readonly<Record<string, number>>;
};

export const TILE_SHAPES = ["square", "circle", "mixed"] as const;
export type TileShape = (typeof TILE_SHAPES)[number];

/** Corner bits of a tile; a set bit is a round corner. */
export const CORNER_TL = 1;
export const CORNER_TR = 2;
export const CORNER_BR = 4;
export const CORNER_BL = 8;
export const ALL_CORNERS = CORNER_TL | CORNER_TR | CORNER_BR | CORNER_BL;

/**
 * The corners after a click at (fx, fy), fractions of the tile (0,0 = top
 * left). A click in the centre makes all corners round, or all sharp when
 * they are all round already. A click elsewhere changes the nearest corner.
 */
export function toggleCorner(corners: number, fx: number, fy: number): number {
  if (Math.abs(fx - 0.5) < 0.2 && Math.abs(fy - 0.5) < 0.2) {
    return corners === ALL_CORNERS ? 0 : ALL_CORNERS;
  }
  const bit = fy < 0.5 ? (fx < 0.5 ? CORNER_TL : CORNER_TR) : fx < 0.5 ? CORNER_BL : CORNER_BR;
  return corners ^ bit;
}

/**
 * The corner radii [top left, top right, bottom right, bottom left] of a
 * (w × h) tile, in the order of CanvasRenderingContext2D.roundRect. roundRect
 * scales radii that do not fit, so two round corners on one side meet as a
 * half disc and four give a circle.
 */
export function cornerRadii(corners: number, w: number, h: number, radius: number): number[] {
  const r = (Math.min(w, h) * radius) / 100;
  return [CORNER_TL, CORNER_TR, CORNER_BR, CORNER_BL].map((bit) => (corners & bit ? r : 0));
}

/** Stable per-cell identity. Grid-coordinate based, matching the (row, col)
 * keying of the mosaic RNG — so a clicked cell maps to the same square in both
 * the base and @2x splash variants. */
export function cellKey(row: number, col: number): string {
  return `${row}:${col}`;
}

// Click-to-cycle opacity steps (percent). Each click on a square advances it
// one step. A square that was VISIBLE before any clicks starts by hiding (0%);
// one that was HIDDEN (dropped by the fade or ramped to nothing) starts by
// appearing (25%). Both then climb in 25% steps and wrap around.
const CYCLE_FROM_VISIBLE = [0, 25, 50, 75, 100];
const CYCLE_FROM_HIDDEN = [25, 50, 75, 100, 0];

/** Resolve the override opacity (percent) for a cell from its click count and
 * whether it was visible before any clicks. `clicks` is 1-based. */
export function overridePercent(clicks: number, baseVisible: boolean): number {
  const cycle = baseVisible ? CYCLE_FROM_VISIBLE : CYCLE_FROM_HIDDEN;
  return cycle[(clicks - 1) % cycle.length];
}

type CellPaint = {
  painted: boolean;
  alpha: number;
  colorIndex: number;
  xL: number;
  yT: number;
  w: number;
  h: number;
  /** Round corners (CORNER_* bits). */
  corners: number;
};

const NOT_PAINTED: CellPaint = {
  painted: false,
  alpha: 0,
  colorIndex: 0,
  xL: 0,
  yT: 0,
  w: 0,
  h: 0,
  corners: 0,
};


/**
 * Resolve one cell's appearance — geometry, color index, alpha, and whether a
 * square is painted at all. Single source of truth shared by drawPixelation
 * (to render) and getCellAt (to hit-test clicks), so a click targets exactly
 * the squares that are drawn. `paletteLen` is passed in so the color index is
 * resolvable without the palette array.
 */
function computeCell(
  row: number,
  col: number,
  size: number,
  width: number,
  height: number,
  cellSeed: number,
  paletteLen: number,
  params: PixelationParams
): CellPaint {
  const dir = params.direction || "ltr";
  // "btt" (square/portrait): coverage runs up the height, columns span the full
  // width, and rows stack upward from the bottom edge. "ttb": same but from top.
  // "ltr" (landscape): coverage runs across the width, rows span the full height,
  // columns march rightward from the left edge. "rtl": same but from right.
  // `t` is 0 at the solid edge, 1 at the fade edge.
  const vertical = dir === "btt" || dir === "ttb";
  const coverageExtent = vertical
    ? (height * params.coverage) / 100
    : (width * params.coverage) / 100;
  // Cells may be slightly non-square: `size` is the dissolve-axis (fade) edge,
  // `sizeCross` the full-span edge. The fade axis carries the SHARED cell count
  // (so patterns align across templates) while the cross axis is fit to its own
  // extent (so no tile is cropped). Map both to X/Y by orientation.
  const sizeCross = params.sizeCross ?? size;
  const sizeX = vertical ? sizeCross : size;
  const sizeY = vertical ? size : sizeCross;
  const along = (vertical ? row : col) * size + size / 2;
  const t = Math.min(1, Math.max(0, along / coverageExtent));

  // Key the per-cell RNG on the cell's (fadeIndex, crossIndex), where fadeIndex
  // runs along the dissolve axis (0 at the solid edge) and crossIndex spans the
  // band. Because every template uses the SAME number of cells along the fade
  // (PixelationLayer sizes squares from a shared fade-cell count), a given
  // fadeIndex sits at the same point in the dissolve on every template — so the
  // scattered dither (which squares drop to transparent, each square's color)
  // lines up across templates and is identical for the base/@2x splash variants.
  const fadeIdx = vertical ? row : col;
  const crossIdx = vertical ? col : row;
  const cellRng = mulberry32(hashInts(cellSeed, fadeIdx, crossIdx));
  const rPick = cellRng();
  const rVis = cellRng();
  const rJit = cellRng();
  // The corner draws come after the first three, so they do not change the
  // colours, dropout or jitter of a mosaic saved before tile shapes.
  const rCorners = [cellRng(), cellRng(), cellRng(), cellRng()];

  // Snap cell edges to integer pixels so neighbours share an exact boundary
  // — no sub-pixel gap, and crucially no 1px overlap (overlapping cells
  // double their alpha at the seam, which shows as borders at <100% opacity).
  const xL = dir === "rtl" ? Math.round(width - (col + 1) * sizeX) : Math.round(col * sizeX);
  const xR = dir === "rtl" ? Math.round(width - col * sizeX) : Math.round(col * sizeX + sizeX);
  // For "btt" the row index grows upward from the bottom. For "ttb" it grows
  // downward from the top.
  const yT = dir === "btt" ? Math.round(height - (row + 1) * sizeY)
    : dir === "ttb" ? Math.round(row * sizeY)
    : Math.round(row * sizeY);
  const yB = dir === "btt" ? Math.round(height - row * sizeY)
    : dir === "ttb" ? Math.round(row * sizeY + sizeY)
    : Math.round(row * sizeY + sizeY);
  // Base color index from the cell RNG, then advance by the per-cell color
  // click count so each click cycles green→purple→blue→green through the palette.
  const baseColorIndex = paletteLen ? Math.floor(rPick * paletteLen) % paletteLen : 0;
  const colorClicks = params.colorOverrides?.[cellKey(row, col)] || 0;
  const colorIndex = paletteLen ? (baseColorIndex + colorClicks) % paletteLen : 0;
  // The generated corners, unless a click set this tile's corners.
  const shape = params.tileShape || "square";
  let corners = 0;
  if (shape === "circle") corners = ALL_CORNERS;
  else if (shape === "mixed") {
    const chance = (params.tileRoundness ?? 50) / 100;
    rCorners.forEach((r, i) => {
      if (r < chance) corners |= 1 << i;
    });
  }
  const shapeOverride = params.shapeOverrides?.[cellKey(row, col)];
  if (shapeOverride !== undefined) corners = shapeOverride;
  const geom = {colorIndex, xL, yT, w: xR - xL, h: yB - yT, corners};

  // ── Base (un-clicked) appearance ──────────────────────────────────────
  // The generated alpha from opacity slider + fade dropout + ramp.
  let baseAlpha = params.opacity / 100;
  let basePainted = true;
  if (params.gradual) {
    // Probability of a square showing falls off L→R across the coverage band,
    // producing the scattered dissolve edge. Surviving squares also get a mild
    // alpha taper + per-cell jitter for a dithered look.
    if (rVis > 1 - t) {
      basePainted = false;
      baseAlpha = 0;
    } else {
      baseAlpha *= (0.65 + 0.35 * (1 - t)) * (0.8 + 0.2 * rJit);
    }
  }
  if (basePainted && params.opacityRamp) {
    // Smooth per-column transparency decrease: full opacity at the left edge,
    // dissolving to zero at the coverage edge. Quadratic so the left stays
    // solid longer and the fade tail is gentle.
    baseAlpha *= (1 - t) * (1 - t);
  }

  // ── Click override ────────────────────────────────────────────────────
  // A clicked square ignores the generated alpha and cycles through fixed 25%
  // steps. Direction depends on whether it was visible before any clicks: a
  // visible square hides first (0%), a hidden one appears first (25%).
  const clicks = params.overrides?.[cellKey(row, col)];
  if (clicks) {
    const baseVisible = basePainted && baseAlpha > 0.0005;
    const pct = overridePercent(clicks, baseVisible);
    return pct > 0 ? {painted: true, alpha: pct / 100, ...geom} : {...NOT_PAINTED, ...geom};
  }

  return basePainted && baseAlpha > 0
    ? {painted: true, alpha: baseAlpha, ...geom}
    : {...NOT_PAINTED, ...geom};
}

/**
 * The box and the corners of one tile, painted or not. Used to find which
 * corner of a tile a click is on.
 */
export function describeCell(
  row: number,
  col: number,
  width: number,
  height: number,
  params: PixelationParams
): {xL: number; yT: number; w: number; h: number; corners: number} {
  const {xL, yT, w, h, corners} = computeCell(
    row,
    col,
    params.size,
    width,
    height,
    params.seed >>> 0,
    params.palette.length,
    params
  );
  return {xL, yT, w, h, corners};
}

/**
 * Paint the mosaic onto a 2D context sized to (width × height) base pixels.
 * Clears first, so it's safe to call repeatedly on the same canvas. No-op when
 * the palette is empty.
 */
export function drawPixelation(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  params: PixelationParams
): void {
  ctx.clearRect(0, 0, width, height);
  const {size, coverage, palette, seed} = params;
  const sizeCross = params.sizeCross ?? size;
  // Don't gate on opacity here: a clicked square can be visible even when the
  // opacity slider is 0 (the click overrides it), so the grid must still draw.
  if (!palette.length || size <= 0 || sizeCross <= 0 || coverage <= 0) return;

  const dir = params.direction || "ltr";
  // The coverage axis depends on direction: "btt"/"ttb" span rows up/down the
  // height, "ltr"/"rtl" span columns across the width (see computeCell). The
  // other axis fills the full canvas extent. `size` is the fade-axis edge,
  // `sizeCross` the full-span edge — mapped to X/Y by orientation.
  const vertical = dir === "btt" || dir === "ttb";
  const coverageExtent = vertical ? (height * coverage) / 100 : (width * coverage) / 100;
  const sizeX = vertical ? sizeCross : size;
  const sizeY = vertical ? size : sizeCross;
  const cols = vertical ? Math.ceil(width / sizeX) : Math.ceil(coverageExtent / sizeX);
  const rows = vertical ? Math.ceil(coverageExtent / sizeY) : Math.ceil(height / sizeY);
  const rgb = palette.map(hexToRgb);

  // Per-cell randomness is keyed on the cell's (fadeIndex, crossIndex) (see
  // computeCell), NOT on pixel size. Because every template shares the fade-cell
  // count, a given fadeIndex is the same point in the dissolve everywhere, so the
  // scattered dither lines up across templates and is identical for the base/@2x
  // splash variants. Each cell draws 3 values from its own mulberry32.
  //
  // The palette is deliberately NOT folded into the seed: the per-cell color is
  // chosen by the first draw (rPick) mapped through the palette, while the fade
  // dropout (rVis) and jitter (rJit) come from later draws. Keeping the seed
  // palette-independent means changing colors — switching Background/Product/
  // Custom, or editing a Custom swatch — re-tints the existing mosaic without
  // disturbing which squares drop out or their opacity. Only `seed`
  // (Regenerate) reshuffles the layout.
  const cellSeed = seed >>> 0;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const cell = computeCell(row, col, size, width, height, cellSeed, rgb.length, params);
      if (!cell.painted) continue;
      const m = params.mask
        ? Math.min(1, params.mask(cell.xL + cell.w / 2, cell.yT + cell.h / 2))
        : 1;
      if (m <= 0) continue;
      const {r, g, b} = rgb[cell.colorIndex];
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${(cell.alpha * m).toFixed(3)})`;
      if (cell.corners) {
        ctx.beginPath();
        ctx.roundRect(
          cell.xL,
          cell.yT,
          cell.w,
          cell.h,
          cornerRadii(cell.corners, cell.w, cell.h, params.tileRadius ?? 50)
        );
        ctx.fill();
      } else {
        ctx.fillRect(cell.xL, cell.yT, cell.w, cell.h);
      }
    }
  }
}

/**
 * Map a pixel coordinate (in canvas backing-store space) to its grid cell, or
 * null if the point is outside the coverage band or off the grid. Pure
 * geometry — it does NOT require a square to be currently painted there, so a
 * click can reveal a cell the fade/ramp had hidden (first click → 25%). Mirrors
 * drawPixelation's grid so clicks line up with what's on screen.
 */
export function getCellAt(
  px: number,
  py: number,
  width: number,
  height: number,
  params: PixelationParams
): {row: number; col: number} | null {
  const {size, coverage, palette} = params;
  const sizeCross = params.sizeCross ?? size;
  if (!palette.length || size <= 0 || sizeCross <= 0 || coverage <= 0) return null;

  const dir = params.direction || "ltr";
  const vertical = dir === "btt" || dir === "ttb";
  const coverageExtent = vertical ? (height * coverage) / 100 : (width * coverage) / 100;
  // `size` is the fade-axis edge, `sizeCross` the full-span edge — mapped to X/Y
  // by orientation, mirroring computeCell so clicks land on the drawn squares.
  const sizeX = vertical ? sizeCross : size;
  const sizeY = vertical ? size : sizeCross;

  if (vertical) {
    if (dir === "btt") {
      // Band hugs the bottom edge and spans the full width; row 0 is bottom-most.
      if (px < 0 || px >= width || py >= height || py < height - coverageExtent) return null;
      const col = Math.floor(px / sizeX);
      const row = Math.floor((height - py) / sizeY);
      if (col < 0 || col >= Math.ceil(width / sizeX)) return null;
      if (row < 0 || row >= Math.ceil(coverageExtent / sizeY)) return null;
      return {row, col};
    }
    // "ttb": Band hugs the top edge and spans the full width; row 0 is top-most.
    if (px < 0 || px >= width || py < 0 || py >= coverageExtent) return null;
    const col = Math.floor(px / sizeX);
    const row = Math.floor(py / sizeY);
    if (col < 0 || col >= Math.ceil(width / sizeX)) return null;
    if (row < 0 || row >= Math.ceil(coverageExtent / sizeY)) return null;
    return {row, col};
  }

  if (dir === "ltr") {
    // Band hugs the left edge and spans the full height; col 0 is left-most.
    if (px < 0 || py < 0 || px >= coverageExtent || py >= height) return null;
    const col = Math.floor(px / sizeX);
    const row = Math.floor(py / sizeY);
    if (col < 0 || col >= Math.ceil(coverageExtent / sizeX)) return null;
    if (row < 0 || row >= Math.ceil(height / sizeY)) return null;
    return {row, col};
  }

  // "rtl": Band hugs the right edge and spans the full height; col 0 is right-most.
  if (px < width - coverageExtent || px >= width || py < 0 || py >= height) return null;
  const col = Math.floor((width - px) / sizeX);
  const row = Math.floor(py / sizeY);
  if (col < 0 || col >= Math.ceil(coverageExtent / sizeX)) return null;
  if (row < 0 || row >= Math.ceil(height / sizeY)) return null;

  return {row, col};
}
